import { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Camera } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "../ui/sonner";
import { mediaUrl } from "../../lib/mediaUrl";

const FeaturedRow = ({ p }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} data-testid={`featured-order-row-${p.id}`} className="flex items-center gap-3 border border-soft-gray rounded-xl p-3 bg-clean-white">
      <button {...attributes} {...listeners} className="cursor-grab text-midnight-black/40 shrink-0" data-testid={`featured-order-drag-${p.id}`}>
        <GripVertical className="w-4 h-4" />
      </button>
      {p.profile_photo_url ? (
        <img src={mediaUrl(p.profile_photo_url)} alt={p.business_name} className="w-10 h-10 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-warm-white border border-soft-gray flex items-center justify-center shrink-0">
          <Camera className="w-4 h-4 text-midnight-black/30" />
        </div>
      )}
      <div>
        <p className="font-bold text-sm">{p.business_name || `${p.first_name} ${p.last_name}`}</p>
        <p className="text-xs text-midnight-black/50">{p.location || "No location set"}{!p.verified && " · Not verified — won't show publicly"}</p>
      </div>
    </div>
  );
};

/** Drag-and-drop control for the order Featured Photographers appear on the homepage. */
export const FeaturedOrderManager = () => {
  const [items, setItems] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = () => {
    api.get("/admin/photographers/featured").then(({ data }) => setItems(data));
  };

  useEffect(() => {
    load();
  }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    try {
      await api.put("/admin/photographers/featured-order", { order: reordered.map((i) => i.id) });
      toast.success("Order saved");
    } catch (err) {
      toast.error("Couldn't save order");
      load();
    }
  };

  if (items === null || items.length === 0) return null;

  return (
    <div className="border-2 border-stranger-orange/30 bg-warm-white rounded-2xl p-6 mb-8" data-testid="featured-order-manager">
      <h2 className="font-extrabold uppercase text-sm mb-1">Featured Homepage Order</h2>
      <p className="text-xs font-medium text-midnight-black/50 mb-4">Drag to reorder — this is the order they'll appear in on the homepage.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((p) => (
              <FeaturedRow key={p.id} p={p} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
